import React, { useState, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { TipoAudiencia, CategoriaNotificacion, NotificacionRequest } from 'types/notificacion.types';
import { notificacionService } from 'services/notificacionService';
import { catalogoService, usuarioService } from 'services/usuarioService';
import { ROL_ID } from 'types/usuario.types';
import { Grado, Seccion, UsuarioListItem } from 'types/usuario.types';
import { catalogStorage } from 'utils/catalogStorage';
import RichTextEditor from 'components/RichTextEditor';
import { I, PageHead } from 'components/ui';

// ─────────────────────────────────────────────────────────────────
// CrearNotificacionPage
// Formulario de creación con selector de audiencia condicional
// ─────────────────────────────────────────────────────────────────

interface Props {
  onCancel:  () => void;
  onSuccess: () => void;
}

const NIVELES = ['INICIAL', 'PRIMARIA', 'SECUNDARIA'] as const;

// IDs resueltos desde el caché del backend (cargado al login); fallback al mapa estático
const rolId = (nombre: keyof typeof ROL_ID) =>
  catalogStorage.getIdRol(nombre, ROL_ID[nombre]);

const ROL_OPTIONS = [
  { label: 'Administrador',  value: rolId('ADMIN') },
  { label: 'Director',       value: rolId('DIRECTOR') },
  { label: 'Secretaria',     value: rolId('SECRETARIA') },
  { label: 'Docente',        value: rolId('DOCENTE') },
  { label: 'Estudiante',     value: rolId('ESTUDIANTE') },
  { label: 'Padre',          value: rolId('PADRE') },
];

const Ico = ({ d, s = 'w-5 h-5' }: { d: string; s?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className={s}>
    <path strokeLinecap="round" strokeLinejoin="round" d={d}/>
  </svg>
);
const D_BACK  = 'M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18';
const D_SEND  = 'M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5';
const D_CHECK = 'M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z';

const Spin = () => (
  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const Label = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
    {children} {required && <span className="text-red-500">*</span>}
  </label>
);

const Input = (props: React.InputHTMLAttributes<HTMLInputElement> & { error?: string }) => {
  const { error, ...rest } = props;
  return (
    <div>
      <input {...rest}
        className={`w-full px-3 py-2.5 rounded-xl border text-sm text-slate-800
                    outline-none focus:ring-2 transition-all
                    ${error
                      ? 'border-red-300 focus:ring-red-400'
                      : 'border-slate-200 focus:ring-blue-500'}`}/>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};

const Select = (props: React.SelectHTMLAttributes<HTMLSelectElement> & { error?: string }) => {
  const { error, children, ...rest } = props;
  return (
    <div>
      <select {...rest}
        className={`w-full px-3 py-2.5 rounded-xl border text-sm text-slate-800
                    outline-none focus:ring-2 transition-all bg-white
                    ${error
                      ? 'border-red-300 focus:ring-red-400'
                      : 'border-slate-200 focus:ring-blue-500'}`}>
        {children}
      </select>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};

const CrearNotificacionPage: React.FC<Props> = ({ onCancel, onSuccess }) => {
  const { user } = useAuth();
  const rol = user?.rol ?? 'DOCENTE';

  // Tipos de audiencia disponibles según rol
  const audienciasPermitidas: TipoAudiencia[] =
    rol === 'DOCENTE'
      ? ['SECCION', 'INDIVIDUAL']
      : rol === 'SECRETARIA'
        ? ['NIVEL', 'GRADO', 'SECCION', 'INDIVIDUAL']
        : ['TODOS', 'ROL', 'NIVEL', 'GRADO', 'SECCION', 'INDIVIDUAL'];

  // Form state
  const [titulo,               setTitulo]               = useState('');
  const [mensaje,              setMensaje]              = useState('');
  const [categoria,            setCategoria]            = useState<CategoriaNotificacion>('COMUNICADO');
  const [requiereConfirmacion, setRequiereConfirmacion] = useState(false);
  const [adjuntoUrl,           setAdjuntoUrl]           = useState('');
  const [tipoAudiencia,        setTipoAudiencia]        = useState<TipoAudiencia>(audienciasPermitidas[0]);
  const [rolId,                setRolId]                = useState('');
  const [nivel,                setNivel]                = useState('');
  const [gradoId,              setGradoId]              = useState('');
  const [seccionId,            setSeccionId]            = useState('');
  const [destinatarioId,       setDestinatarioId]       = useState('');
  const [fechaExpiracion,      setFechaExpiracion]      = useState('');
  const [errors,               setErrors]               = useState<Record<string, string>>({});
  const [submitting,           setSubmitting]           = useState(false);
  const [submitError,          setSubmitError]          = useState<string | null>(null);
  const [success,              setSuccess]              = useState(false);

  // Catálogos
  const [grados,       setGrados]       = useState<Grado[]>([]);
  const [secciones,    setSecciones]    = useState<Seccion[]>([]);
  const [estudiantes,  setEstudiantes]  = useState<UsuarioListItem[]>([]);
  const [busqueda,     setBusqueda]     = useState('');
  const [buscando,     setBuscando]     = useState(false);

  useEffect(() => {
    catalogoService.getGrados().then(setGrados).catch(() => {});
    catalogoService.getSecciones().then(setSecciones).catch(() => {});
  }, []);

  // Reset campos condicionales al cambiar audiencia
  useEffect(() => {
    setRolId(''); setNivel(''); setGradoId('');
    setSeccionId(''); setDestinatarioId('');
    setBusqueda(''); setEstudiantes([]);
    setErrors({});
  }, [tipoAudiencia]);

  // Filtrar grados por nivel seleccionado
  const gradosFiltrados = nivel
    ? grados.filter(g => g.nivel === nivel)
    : grados;

  const seccionesFiltradas = gradoId
    ? secciones.filter(s => s.gradoId === Number(gradoId))
    : secciones;

  const buscarDestinatario = async () => {
    if (!busqueda.trim()) return;
    setBuscando(true);
    try {
      const data = await usuarioService.listarEstudiantes({ search: busqueda });
      setEstudiantes(Array.isArray(data) ? data : []);
    } catch {} finally { setBuscando(false); }
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!titulo.trim())   e.titulo  = 'Requerido';
    if (!mensaje.trim())  e.mensaje = 'Requerido';
    else if (new TextEncoder().encode(mensaje).length > 65535)
      e.mensaje = 'El mensaje supera el límite de 65,535 bytes';
    if (tipoAudiencia === 'ROL'        && !rolId)          e.rolId          = 'Selecciona un rol';
    if (tipoAudiencia === 'NIVEL'      && !nivel)          e.nivel          = 'Selecciona un nivel';
    if (tipoAudiencia === 'GRADO'      && !gradoId)        e.gradoId        = 'Selecciona un grado';
    if (tipoAudiencia === 'SECCION'    && !seccionId)      e.seccionId      = 'Selecciona una sección';
    if (tipoAudiencia === 'INDIVIDUAL' && !destinatarioId) e.destinatarioId = 'Selecciona un destinatario';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: NotificacionRequest = {
        titulo:   titulo.trim(),
        mensaje:  mensaje.trim(),
        categoria,
        requiereConfirmacion: categoria === 'COMUNICADO' ? requiereConfirmacion : false,
        adjuntoUrl: adjuntoUrl.trim() || null,
        tipoAudiencia,
        rolId:             tipoAudiencia === 'ROL'        ? Number(rolId)          : null,
        nivel:             tipoAudiencia === 'NIVEL'      ? nivel                  : null,
        gradoId:           tipoAudiencia === 'GRADO'      ? Number(gradoId)        : null,
        seccionId:         tipoAudiencia === 'SECCION'    ? Number(seccionId)      : null,
        usuarioDestinoId:  tipoAudiencia === 'INDIVIDUAL' ? Number(destinatarioId) : null,
        fechaExpiracion:   fechaExpiracion || null,
      };
      await notificacionService.crear(payload);
      setSuccess(true);
      setTimeout(onSuccess, 1500);
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al crear notificación');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center">
          <Ico d={D_CHECK} s="w-8 h-8 text-green-600"/>
        </div>
        <p className="text-lg font-bold text-slate-800">Notificación enviada</p>
        <p className="text-slate-400 text-sm">Redirigiendo…</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <PageHead
        title="Nueva Notificación"
        sub="Redacta el mensaje y define la audiencia"
        actions={
          <button onClick={onCancel} className="btn btn-ghost">
            <I.ChevronLeft size={14} /> Volver
          </button>
        }
      />

      <div className="card overflow-hidden">
        <div
          className="px-6 py-4 text-white relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #1e1b4b 0%, #4338ca 50%, #0d9488 100%)',
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(circle at 80% 20%, rgba(20,184,166,.25) 0%, transparent 40%)' }}
          />
          <div className="relative">
            <h2 className="text-white font-bold text-base m-0">Redactar mensaje</h2>
            <p className="text-white/70 text-xs mt-0.5 m-0">
              El mensaje llegará a los usuarios según la audiencia seleccionada
            </p>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Título */}
          <div className="flex flex-col gap-1.5">
            <Label required>Título</Label>
            <Input
              value={titulo} onChange={e => setTitulo(e.target.value)}
              placeholder="Ej: Inicio del año escolar 2026"
              error={errors.titulo}
              maxLength={120}
            />
          </div>

          {/* Mensaje */}
          <div className="flex flex-col gap-1.5">
            <Label required>Mensaje</Label>
            <RichTextEditor
              value={mensaje}
              onChange={setMensaje}
              placeholder="Redacta el contenido de la notificación…"
              maxBytes={65535}
              error={errors.mensaje}
            />
          </div>

          {/* Categoría */}
          <div className="flex flex-col gap-1.5">
            <Label required>Categoría</Label>
            <div className="flex gap-2">
              {(['COMUNICADO', 'ALERTA', 'RECORDATORIO'] as CategoriaNotificacion[]).map(cat => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => {
                    setCategoria(cat);
                    if (cat !== 'COMUNICADO') setRequiereConfirmacion(false);
                  }}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold uppercase tracking-wide
                              border transition-colors
                              ${categoria === cat
                                ? cat === 'COMUNICADO'   ? 'bg-blue-700 border-blue-700 text-white'
                                  : cat === 'ALERTA'     ? 'bg-red-600 border-red-600 text-white'
                                  :                        'bg-amber-500 border-amber-500 text-white'
                                : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
                >
                  {cat === 'COMUNICADO' ? 'Comunicado' : cat === 'ALERTA' ? 'Alerta' : 'Recordatorio'}
                </button>
              ))}
            </div>
          </div>

          {/* Requiere confirmación — solo para COMUNICADO */}
          {categoria === 'COMUNICADO' && (
            <div className="flex items-center justify-between rounded-xl border border-slate-200
                            bg-slate-50 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-slate-700">Requiere confirmación de lectura</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Los destinatarios verán un botón "He leído y entendido"
                </p>
              </div>
              <button
                type="button"
                onClick={() => setRequiereConfirmacion(v => !v)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                  ${requiereConfirmacion ? 'bg-blue-600' : 'bg-slate-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow
                  transition-transform ${requiereConfirmacion ? 'translate-x-6' : 'translate-x-1'}`}/>
              </button>
            </div>
          )}

          {/* URL de adjunto */}
          <div className="flex flex-col gap-1.5">
            <Label>Documento adjunto <span className="font-normal text-slate-400 normal-case">(opcional)</span></Label>
            <Input
              type="url"
              value={adjuntoUrl}
              onChange={e => setAdjuntoUrl(e.target.value)}
              placeholder="https://drive.google.com/... o URL del documento"
            />
            <p className="text-xs text-slate-400">
              Enlace a un documento o archivo (Google Drive, PDF, etc.)
            </p>
          </div>

          {/* Tipo de audiencia */}
          <div className="flex flex-col gap-1.5">
            <Label required>Audiencia</Label>
            <Select
              value={tipoAudiencia}
              onChange={e => setTipoAudiencia(e.target.value as TipoAudiencia)}
            >
              {audienciasPermitidas.map(a => (
                <option key={a} value={a}>
                  { a === 'TODOS'      ? 'Todos los usuarios'
                  : a === 'ROL'       ? 'Por Rol'
                  : a === 'NIVEL'     ? 'Por Nivel'
                  : a === 'GRADO'     ? 'Por Grado'
                  : a === 'SECCION'   ? 'Por Sección'
                  :                    'Individual (usuario específico)'}
                </option>
              ))}
            </Select>
          </div>

          {/* ── Condicionales de audiencia ── */}

          {tipoAudiencia === 'ROL' && (
            <div className="flex flex-col gap-1.5">
              <Label required>Rol</Label>
              <Select value={rolId} onChange={e => setRolId(e.target.value)} error={errors.rolId}>
                <option value="">Seleccionar rol…</option>
                {ROL_OPTIONS.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </Select>
            </div>
          )}

          {(tipoAudiencia === 'NIVEL' || tipoAudiencia === 'GRADO' || tipoAudiencia === 'SECCION') && (
            <div className="flex flex-col gap-1.5">
              <Label required={tipoAudiencia === 'NIVEL'}>Nivel</Label>
              <Select
                value={nivel}
                onChange={e => { setNivel(e.target.value); setGradoId(''); setSeccionId(''); }}
                error={errors.nivel}
              >
                <option value="">Seleccionar nivel…</option>
                {NIVELES.map(n => <option key={n} value={n}>{n}</option>)}
              </Select>
            </div>
          )}

          {(tipoAudiencia === 'GRADO' || tipoAudiencia === 'SECCION') && nivel && (
            <div className="flex flex-col gap-1.5">
              <Label required={tipoAudiencia === 'GRADO'}>Grado</Label>
              <Select
                value={gradoId}
                onChange={e => { setGradoId(e.target.value); setSeccionId(''); }}
                error={errors.gradoId}
                disabled={!nivel}
              >
                <option value="">Seleccionar grado…</option>
                {gradosFiltrados.map(g => (
                  <option key={g.idGrado} value={g.idGrado}>{g.nombre} – {g.nivel}</option>
                ))}
              </Select>
            </div>
          )}

          {tipoAudiencia === 'SECCION' && gradoId && (
            <div className="flex flex-col gap-1.5">
              <Label required>Sección</Label>
              <Select value={seccionId} onChange={e => setSeccionId(e.target.value)} error={errors.seccionId}>
                <option value="">Seleccionar sección…</option>
                {seccionesFiltradas.map(s => (
                  <option key={s.idSeccion} value={s.idSeccion}>Sección {s.nombre}</option>
                ))}
              </Select>
            </div>
          )}

          {tipoAudiencia === 'INDIVIDUAL' && (
            <div className="flex flex-col gap-1.5">
              <Label required>Destinatario</Label>
              <div className="flex gap-2">
                <input
                  value={busqueda} onChange={e => setBusqueda(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && buscarDestinatario()}
                  placeholder="Buscar por nombre o DNI…"
                  className="flex-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm
                             outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                />
                <button onClick={buscarDestinatario} disabled={buscando || !busqueda.trim()}
                  className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm
                             font-semibold text-slate-600 transition-colors disabled:opacity-50">
                  {buscando ? <Spin/> : 'Buscar'}
                </button>
              </div>

              {estudiantes.length > 0 && !destinatarioId && (
                <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
                  {estudiantes.map(u => (
                    <button key={u.idUsuario} onClick={() => {
                      setDestinatarioId(String(u.idUsuario));
                      setBusqueda(`${u.nombres} ${u.apellidos}`);
                      setEstudiantes([]);
                    }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50
                                 transition-colors text-left text-sm">
                      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700
                                      font-bold text-xs flex items-center justify-center flex-shrink-0">
                        {u.nombres.charAt(0)}{u.apellidos.charAt(0)}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{u.apellidos}, {u.nombres}</p>
                        <p className="text-xs text-slate-400">DNI: {u.dni}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {destinatarioId && (
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2">
                  <Ico d={D_CHECK} s="w-4 h-4 text-blue-600"/>
                  <p className="text-sm text-blue-700 font-semibold flex-1">{busqueda}</p>
                  <button onClick={() => { setDestinatarioId(''); setBusqueda(''); }}
                    className="text-blue-400 hover:text-blue-600 text-xs">Cambiar</button>
                </div>
              )}
              {errors.destinatarioId && <p className="text-xs text-red-500">{errors.destinatarioId}</p>}
            </div>
          )}

          {/* Fecha de expiración */}
          <div className="flex flex-col gap-1.5">
            <Label>Fecha de expiración <span className="font-normal text-slate-400 normal-case">(opcional)</span></Label>
            <Input
              type="date"
              value={fechaExpiracion}
              onChange={e => setFechaExpiracion(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
            <p className="text-xs text-slate-400">
              Si no se especifica, la notificación no expira.
            </p>
          </div>

          {submitError && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">
              {submitError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-foot">
          <button onClick={onCancel} className="btn btn-ghost">
            Cancelar
          </button>
          <button onClick={handleSubmit} disabled={submitting} className="btn btn-primary">
            {submitting ? <Spin /> : <I.Send size={14} />}
            Enviar notificación
          </button>
        </div>
      </div>
    </div>
  );
};

export default CrearNotificacionPage;
